// Base AI Tool - Class-based AI tool implementation
// Provides standard helpers and checkpoint access via AI tool set

export class BaseAITool {
  constructor() {
    this.toolId = null;
    this.definition = null;
    this.aiToolSystem = null;
    this.aiToolSet = null;
    this.bubbleStyle = "simple";
  }

  get toolSystem() {
    return this.aiToolSystem?.toolSystem || null;
  }

  get aiInferenceSystem() {
    return this.aiToolSystem?.aiInferenceSystem || null;
  }

  /**
   * Convenience access to the active Chat instance.
   * @returns {import("../chat/Chat.js").Chat|null}
   */
  getChat() {
    return this.aiInferenceSystem?.chat || null;
  }

  // ---------------------------------------------------------------------------
  // Chat instance tool data helpers
  //
  // Tools that store state on the Chat instance (rather than externally, like
  // file tools do) should read/write through these wrappers so persistence
  // is triggered via AIInferenceSystem.onChatModified.
  // ---------------------------------------------------------------------------

  getInstanceToolData(namespace) {
    return this.aiInferenceSystem?.getInstanceToolData?.(namespace) ?? null;
  }

  setInstanceToolData(namespace, value) {
    return (
      this.aiInferenceSystem?.setInstanceToolData?.(namespace, value) || false
    );
  }

  deleteInstanceToolData(namespace) {
    return (
      this.aiInferenceSystem?.deleteInstanceToolData?.(namespace) || false
    );
  }

  getInstanceToolCallData(namespace, toolCallId) {
    return (
      this.aiInferenceSystem?.getInstanceToolCallData?.(
        namespace,
        toolCallId
      ) ?? null
    );
  }

  setInstanceToolCallData(namespace, toolCallId, value) {
    return (
      this.aiInferenceSystem?.setInstanceToolCallData?.(
        namespace,
        toolCallId,
        value
      ) || false
    );
  }

  deleteInstanceToolCallData(namespace, toolCallId) {
    return (
      this.aiInferenceSystem?.deleteInstanceToolCallData?.(
        namespace,
        toolCallId
      ) || false
    );
  }

  // ---------------------------------------------------------------------------
  // Truncation metadata for the currently-executing tool call
  //
  // When a streaming response hits the output-token cap (or is otherwise cut
  // short) mid-arguments, AIInferenceSystem._finalizeStreamingToolCalls attaches
  // a `truncation` block to the pending tool-call entry and still dispatches
  // the tool with best-effort repaired args. Tools that want to react (e.g.
  // write-file saving the partial content it received and reporting back to
  // the model) can read the block here.
  //
  // Returns null on the happy path (arguments fully received). The block is
  // in-memory only and is never persisted on the Chat instance.
  //
  // @returns {{
  //   wasTruncated: true,
  //   reason: string,
  //   maxOutputTokens: number|null,
  //   summary: string,
  //   rawArguments: string
  // } | null}
  // `summary` is model-oriented; write-file may extend it with next-step text.
  // ---------------------------------------------------------------------------
  getCurrentToolCallTruncation() {
    const ctx = this.aiInferenceSystem?.activeToolContext;
    if (!ctx?.pendingToolCalls) return null;
    for (const toolCall of ctx.pendingToolCalls.values()) {
      if (toolCall.status === "executing" && toolCall.truncation) {
        return toolCall.truncation;
      }
    }
    return null;
  }

  /**
   * During `execute()`, ask the inference core to finish every tool in the current
   * assistant tool wave, persist all tool results, then **not** start another model
   * pass (same semantics as including `__revoltHaltAfterAssistantToolBatch` on the
   * return object — see `revoltInferenceToolDirectives.js`).
   */
  queueHaltInferenceAfterAssistantToolBatch() {
    this.aiInferenceSystem?.queueHaltInferenceAfterAssistantToolBatch?.();
  }

  hasCheckpointEntry(entryId, responseMessageId = null) {
    return (
      this.aiToolSet?.hasCheckpointEntry?.(entryId, responseMessageId) || false
    );
  }

  createCheckpointEntry(entryId, entry, responseMessageId = null) {
    return (
      this.aiToolSet?.createCheckpointEntry?.(
        entryId,
        entry,
        responseMessageId
      ) || false
    );
  }

  createRedoCheckpointEntry(entryId, entry) {
    return (
      this.aiToolSet?.createRedoCheckpointEntry?.(entryId, entry) || false
    );
  }

  // Subclasses must implement
  async execute() {
    throw new Error("execute() not implemented for AI tool");
  }

  escapeToolUiHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  getToolBubbleTitle(functionName = this.toolId) {
    if (!functionName || typeof functionName !== "string") {
      return "Tool";
    }
    return functionName
      .replace(/_/g, "-")
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  renderToolBubbleUi(state = {}) {
    const {
      functionName = this.toolId,
      status = "pending",
      paramsTextSoFar = "",
      args = null,
      result = null,
    } = state;

    const title = this.escapeToolUiHtml(this.getToolBubbleTitle(functionName));
    const statusLabel = this.escapeToolUiHtml(
      String(status || "pending").replace(/_/g, " ")
    );

    let payload = "";
    if (result?.success === false && typeof result?.error === "string") {
      payload = result.error;
    } else if (typeof paramsTextSoFar === "string" && paramsTextSoFar.length > 0) {
      payload = paramsTextSoFar;
    } else if (args != null) {
      try {
        payload = JSON.stringify(args, null, 2);
      } catch {
        payload = String(args);
      }
    } else if (result != null) {
      try {
        payload = JSON.stringify(result, null, 2);
      } catch {
        payload = String(result);
      }
    }

    const payloadHtml =
      payload.trim().length > 0
        ? `<pre class="tool-bubble-default-pre"><code>${this.escapeToolUiHtml(
            payload
          )}</code></pre>`
        : "";

    return `
      <div class="tool-bubble-default tool-bubble-default--${this.escapeToolUiHtml(
        this.bubbleStyle || "simple"
      )}">
        <div class="tool-bubble-default-title-row">
          <span class="tool-bubble-default-title">${title}</span>
          <span class="tool-bubble-default-status">${statusLabel}</span>
        </div>
        ${payloadHtml}
      </div>
    `.trim();
  }

  _updateToolUiFromState(state, uiApi) {
    if (!uiApi?.updateToolUiHtml) return;
    const html = this.renderToolBubbleUi(state);
    if (typeof html === "string") {
      uiApi.updateToolUiHtml(html);
    }
  }

  /**
   * Optional hook: called once when a tool call bubble is created (start boundary).
   * Tools can use the provided `uiApi` to update the tool bubble body via HTML
   * without causing React re-renders (Workspace streaming contract).
   *
   * @param {{
   *   toolCallId?: string,
   *   functionName?: string,
   *   callIndex?: number,
   *   paramsTextSoFar?: string
   * }} stream
   * @param {{ updateToolUiHtml?: (html: string) => void, clearToolUi?: () => void }} uiApi
   */
  onToolBubbleInit(stream, uiApi) {
    this._updateToolUiFromState(
      {
        ...stream,
        status: "accumulating_arguments",
        phase: "init",
      },
      uiApi
    );
  }

  /**
   * Optional hook: called on each streamed tool-argument chunk.
   * Tools can update the tool bubble body via `uiApi.updateToolUiHtml(html)`.
   *
   * @param {{
   *   toolCallId?: string,
   *   functionName?: string,
   *   callIndex?: number,
   *   paramsChunk?: string,
   *   paramsTextSoFar?: string
   * }} stream
   * @param {{ updateToolUiHtml?: (html: string) => void, clearToolUi?: () => void }} uiApi
   */
  onToolBubbleParamsChunk(stream, uiApi) {
    this._updateToolUiFromState(
      {
        ...stream,
        status: "accumulating_arguments",
        phase: "stream",
      },
      uiApi
    );
  }

  onToolBubbleStarted(stream, uiApi) {
    this._updateToolUiFromState(
      {
        ...stream,
        status: "executing",
        phase: "executing",
      },
      uiApi
    );
  }

  /**
   * Optional hook: called when tool execution completes.
   * Tools can update the tool bubble body via `uiApi.updateToolUiHtml(html)`.
   *
   * @param {{
   *   toolCallId?: string,
   *   functionName?: string,
   *   callIndex?: number,
   *   args?: any,
   *   result?: any
   * }} stream
   * @param {{ updateToolUiHtml?: (html: string) => void, clearToolUi?: () => void }} uiApi
   */
  onToolBubbleCompleted(stream, uiApi) {
    this._updateToolUiFromState(
      {
        ...stream,
        status: stream?.result?.success === false ? "failed" : "completed",
        phase: "completed",
      },
      uiApi
    );
  }

  renderHistoricalToolBubbleUi(state = {}) {
    return this.renderToolBubbleUi({
      ...state,
      phase: "history",
    });
  }

  handleToolUiAction(actionId, payload = {}, context = {}) {
    if (!actionId || typeof actionId !== "string") {
      return false;
    }

    if (actionId === "open-path") {
      const path = payload?.path;
      if (path && context?.chatAreaMethods?.handleOpenFileByPath) {
        context.chatAreaMethods.handleOpenFileByPath(path);
        return true;
      }
    }

    if (actionId === "open-url") {
      const url = payload?.url;
      if (url && context?.chatAreaMethods?.handleOpenUrl) {
        context.chatAreaMethods.handleOpenUrl(url);
        return true;
      }
    }

    return false;
  }

  // Optional: override for checkpoint restore
  async restoreEntry() {
    throw new Error("restoreEntry() not implemented for AI tool");
  }

  // Optional: override for redo checkpoint restore
  async restoreRedoEntry() {
    throw new Error("restoreRedoEntry() not implemented for AI tool");
  }
}
